import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { speakingAPI } from '../../api/client'
import AppLayout from '../../components/layout/AppLayout'

const H = ({ style, ...p }: React.HTMLAttributes<HTMLElement>) => (
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", ...style }} {...p} />
)

type ShadowResult = { available: boolean; pronunciationScore: number | null; fluencyScore: number | null; provider: string }

/** LME-3 Shadowing — listen to a model sentence, then repeat it and get scored. */
export default function ShadowingPage() {
  const navigate = useNavigate()
  const { data, isLoading } = useQuery({ queryKey: ['shadow-set'], queryFn: speakingAPI.shadowSet })

  const [idx, setIdx] = useState(0)
  const [speaking, setSpeaking] = useState(false)
  const [recording, setRecording] = useState(false)
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null)
  const [result, setResult] = useState<ShadowResult | null>(null)

  const mediaRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])

  const sentences = data?.sentences ?? []
  const current = sentences[idx]
  const done = sentences.length > 0 && idx >= sentences.length
  const pct = sentences.length > 0 ? Math.round((idx / sentences.length) * 100) : 0

  // Stop any ongoing speech when leaving the sentence / unmounting.
  useEffect(() => () => { try { window.speechSynthesis?.cancel() } catch { /* noop */ } }, [])

  const score = useMutation({
    mutationFn: (blob: Blob) => {
      const fd = new FormData()
      fd.append('audio', blob, 'shadow.webm')
      fd.append('reference', current ?? '')
      return speakingAPI.shadowScore(fd)
    },
    onSuccess: (r) => setResult(r),
  })

  function listen() {
    if (!current) return
    try {
      window.speechSynthesis.cancel()
      const u = new SpeechSynthesisUtterance(current)
      u.lang = 'en-US'
      u.rate = 0.95
      u.onstart = () => setSpeaking(true)
      u.onend = () => setSpeaking(false)
      window.speechSynthesis.speak(u)
    } catch { /* speech synthesis unavailable */ }
  }

  async function toggleRecord() {
    if (recording) {
      mediaRef.current?.stop()
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' })
      mediaRef.current = recorder
      chunksRef.current = []
      setResult(null)
      recorder.ondataavailable = (e) => chunksRef.current.push(e.data)
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' })
        setRecordedUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob) })
        score.mutate(blob)
        stream.getTracks().forEach((t) => t.stop())
        setRecording(false)
      }
      recorder.start()
      setRecording(true)
    } catch {
      alert('Không truy cập được micro. Hãy cấp quyền micro cho trình duyệt.')
    }
  }

  function stopSpeech() {
    try { window.speechSynthesis?.cancel() } catch { /* noop */ }
    setSpeaking(false)
  }

  function goTo(nextIdx: number) {
    stopSpeech()
    setResult(null)
    setRecordedUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null })
    setIdx(nextIdx)
  }

  function next() {
    goTo(idx + 1)
  }

  const TEAL = '#0891b2'

  return (
    <AppLayout>
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <span className="material-symbols-outlined" style={{ color: TEAL, fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>graphic_eq</span>
          <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: TEAL }}>Luyện nói · Shadowing</span>
        </div>
        <H style={{ display: 'block', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '0.5rem' }}>
          Nói <span style={{ color: TEAL, fontStyle: 'italic' }}>nhại theo</span>
        </H>
        <p style={{ color: '#535b71', fontSize: '1rem', lineHeight: 1.6 }}>
          Nghe câu mẫu → lặp lại ngay lập tức. Phương pháp shadowing giúp cải thiện phát âm, nhịp điệu và sự tự tin.
        </p>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}><div className="spinner" /></div>
      ) : data && !data.enabled ? (
        <EmptyCard icon="lock" title="Tính năng đang tắt" text="Chế độ Shadowing hiện chưa được bật cho tài khoản của bạn." />
      ) : sentences.length === 0 ? (
        <EmptyCard icon="inbox" title="Chưa có câu để luyện" text="Chưa có nội dung shadowing. Hãy quay lại sau." />
      ) : done ? (
        <div style={{ background: `linear-gradient(135deg, ${TEAL}, #67e8f9)`, borderRadius: '2rem', padding: '3rem', textAlign: 'center' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎙️</div>
          <H style={{ display: 'block', fontSize: '1.75rem', fontWeight: 900, color: 'white', marginBottom: '0.5rem' }}>Hoàn thành!</H>
          <p style={{ color: 'rgba(255,255,255,0.85)', marginBottom: '2rem' }}>Bạn đã shadowing {sentences.length} câu. Luyện đều mỗi ngày để tiến bộ nhanh hơn.</p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => goTo(0)} style={btn('ghost')}>Luyện lại</button>
            <button onClick={() => navigate('/speaking')} style={btn('solid', TEAL)}>Về luyện Nói →</button>
          </div>
        </div>
      ) : (
        <div style={{ maxWidth: '40rem', margin: '0 auto' }}>
          {/* Progress */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#535b71' }}>
              Nguồn: {data?.title ?? '—'}
            </span>
            <H style={{ fontWeight: 800, color: TEAL }}>{idx + 1} / {sentences.length}</H>
          </div>
          <div style={{ width: '100%', height: '0.375rem', background: '#cffafe', borderRadius: '9999px', marginBottom: '2rem', overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: TEAL, borderRadius: '9999px', transition: 'width 0.5s' }} />
          </div>

          {/* Sentence card */}
          <div style={{ background: 'white', borderRadius: '2rem', padding: '2.5rem', boxShadow: '0 20px 40px rgba(8,145,178,0.08)', textAlign: 'center' }}>
            <p style={{ fontSize: '1.375rem', lineHeight: 1.7, color: '#0f172a', fontWeight: 600, marginBottom: '2rem' }}>
              "{current}"
            </p>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: recordedUrl || result ? '1.5rem' : 0 }}>
              <button onClick={listen} style={btn('outline', TEAL)}>
                <span className="material-symbols-outlined" style={{ fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>{speaking ? 'volume_up' : 'hearing'}</span>
                {speaking ? 'Đang phát…' : 'Nghe mẫu'}
              </button>
              <button
                onClick={toggleRecord}
                disabled={score.isPending}
                style={btn('solid', recording ? '#b41340' : TEAL)}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>{recording ? 'stop_circle' : 'mic'}</span>
                {recording ? 'Dừng ghi' : score.isPending ? 'Đang chấm…' : 'Ghi âm & lặp lại'}
              </button>
            </div>

            {/* Playback of own recording */}
            {recordedUrl && (
              <audio controls src={recordedUrl} style={{ width: '100%', marginBottom: '1rem' }} />
            )}

            {/* Score / acknowledgement */}
            {result && (
              result.available ? (
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <ScoreTile label="Phát âm" value={result.pronunciationScore} />
                  <ScoreTile label="Trôi chảy" value={result.fluencyScore} />
                </div>
              ) : (
                <div style={{ background: '#f0f9ff', borderRadius: '1rem', padding: '0.875rem 1rem', fontSize: '0.875rem', color: '#0369a1' }}>
                  ✅ Đã ghi nhận phần đọc của bạn. Chấm điểm phát âm tự động sẽ sớm ra mắt — hiện hãy tự so sánh với câu mẫu.
                </div>
              )
            )}
          </div>

          {/* Nav */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem' }}>
            <button onClick={() => goTo(Math.max(0, idx - 1))} disabled={idx === 0} style={{ ...btn('ghost'), opacity: idx === 0 ? 0.4 : 1 }}>← Trước</button>
            <button onClick={next} style={btn('solid', TEAL)}>{idx === sentences.length - 1 ? 'Hoàn thành' : 'Câu tiếp →'}</button>
          </div>
        </div>
      )}
    </AppLayout>
  )
}

function ScoreTile({ label, value }: { label: string; value: number | null }) {
  const v = value ?? 0
  const color = v >= 80 ? '#0f9d58' : v >= 60 ? '#b45309' : '#b41340'
  return (
    <div style={{ background: '#f8fafc', borderRadius: '1rem', padding: '1rem 1.5rem', minWidth: '7rem' }}>
      <H style={{ display: 'block', fontSize: '1.75rem', fontWeight: 900, color }}>{value != null ? Math.round(v) : '—'}</H>
      <span style={{ fontSize: '0.75rem', color: '#535b71', fontWeight: 600 }}>{label}</span>
    </div>
  )
}

function EmptyCard({ icon, title, text }: { icon: string; title: string; text: string }) {
  return (
    <div style={{ background: 'white', borderRadius: '2rem', padding: '3rem', textAlign: 'center', boxShadow: '0 10px 30px rgba(0,0,0,0.04)' }}>
      <span className="material-symbols-outlined" style={{ fontSize: '3rem', color: '#a5adc6', display: 'block', marginBottom: '1rem' }}>{icon}</span>
      <H style={{ display: 'block', fontSize: '1.25rem', fontWeight: 800, marginBottom: '0.5rem' }}>{title}</H>
      <p style={{ color: '#535b71' }}>{text}</p>
    </div>
  )
}

function btn(kind: 'solid' | 'outline' | 'ghost', color = '#004be2'): React.CSSProperties {
  const base: React.CSSProperties = {
    padding: '0.875rem 1.75rem', borderRadius: '9999px', fontFamily: "'Space Grotesk', sans-serif",
    fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
    fontSize: '0.9375rem', transition: 'all 0.2s',
  }
  if (kind === 'solid') return { ...base, border: 'none', background: color, color: 'white' }
  if (kind === 'outline') return { ...base, border: `2px solid ${color}`, background: 'transparent', color }
  return { ...base, border: '2px solid #e2e8f0', background: 'transparent', color: '#475569' }
}
